import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { LIVE_ZONES, SOON_ZONES } from "../lib/zones";
import { ZoneMapCanvas } from "./ZoneMapCanvas";
import { Reveal, Chapter } from "./Reveal";

const ZoneChip = ({ zone, active, onClick, votes }) => {
  const testid = `zone-chip-${zone.name.toLowerCase().replace(/\s+/g, "-")}`;
  const cls = zone.live
    ? active
      ? "bg-forest text-bone"
      : "bg-white text-forest hover:bg-fresh hover:text-white"
    : active
    ? "border border-dashed border-forest bg-forest text-bone"
    : "border border-dashed border-forest/25 text-forest/60 hover:border-forest/60 hover:text-forest";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-manrope text-sm font-bold transition-colors duration-300 ${cls}`}
    >
      {zone.live && <MapPin className="h-3.5 w-3.5" />}
      {zone.name}
      {!zone.live && votes !== undefined && (
        <span
          data-testid={`${testid}-votes`}
          className="rounded-full bg-fresh/15 px-2 py-0.5 text-[10px] font-extrabold text-fresh"
        >
          {votes}
        </span>
      )}
    </button>
  );
};

const ZonePicker = ({ active, votes, votedZone, onFly, onVote }) => (
  <div className="mt-10">
    <p className="font-manrope text-xs font-bold uppercase tracking-[0.25em] text-forest/50">Delivering now</p>
    <div className="mt-4 flex flex-wrap gap-2">
      {LIVE_ZONES.map((z) => (
        <ZoneChip key={z.name} zone={z} active={active === z.name} onClick={() => onFly(z)} />
      ))}
    </div>
    <p className="mt-8 font-manrope text-xs font-bold uppercase tracking-[0.25em] text-forest/50">
      Coming soon — tap to vote for your block
    </p>
    <div className="mt-4 flex flex-wrap gap-2">
      {SOON_ZONES.map((z) => (
        <ZoneChip
          key={z.name}
          zone={z}
          active={active === z.name}
          votes={votes[z.name] ?? 0}
          onClick={() => onVote(z)}
        />
      ))}
    </div>
    {votedZone && (
      <p data-testid="zone-vote-note" className="mt-3 font-manrope text-xs font-semibold text-forest/50">
        Your vote: <span className="font-bold text-fresh">{votedZone}</span> — we expand where Calgary points.
      </p>
    )}
  </div>
);

export const ZoneMap = () => {
  const mapRef = useRef(null);
  const [active, setActive] = useState(null);
  const [votes, setVotes] = useState({});
  const [votedZone, setVotedZone] = useState(() => localStorage.getItem("freshly_zone_vote"));

  useEffect(() => {
    axios.get(`${API}/zone-votes`).then((r) => setVotes(r.data)).catch(() => {});
  }, []);

  const flyTo = (zone) => {
    setActive(zone.name);
    mapRef.current?.flyTo(zone.pos, 14, { duration: 1.2 });
  };

  const voteFor = async (zone) => {
    flyTo(zone);
    if (votedZone) {
      if (votedZone !== zone.name) toast.info(`You already voted for ${votedZone}.`);
      return;
    }
    try {
      const { data } = await axios.post(`${API}/zone-votes`, { zone: zone.name });
      setVotes((v) => ({ ...v, [zone.name]: data.votes }));
      setVotedZone(zone.name);
      localStorage.setItem("freshly_zone_vote", zone.name);
      toast.success(`Vote counted — ${zone.name} is at ${data.votes} vote${data.votes === 1 ? "" : "s"}.`);
    } catch {
      toast.error("Couldn't count your vote. Try again.");
    }
  };

  return (
    <section id="zones" className="bg-mist px-6 py-24 md:px-12 lg:py-36">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Chapter number="03" label="The Zone" />
            <h2 className="mt-6 font-syne text-4xl font-extrabold leading-[1.02] tracking-tighter text-forest sm:text-5xl lg:text-6xl">
              Are we on
              <br />
              <span className="text-fresh">your block?</span>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-forest/70 md:text-lg">
              Freshly is live across central Calgary — and spreading fast. Tap a
              neighbourhood to see it on the map, with new zones lighting up
              every month.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <ZonePicker active={active} votes={votes} votedZone={votedZone} onFly={flyTo} onVote={voteFor} />
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-7">
          <ZoneMapCanvas mapRef={mapRef} onSelect={setActive} />
        </Reveal>
      </div>
    </section>
  );
};
